import { useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";
import { useAuth } from "../lib/auth.js";
import { userRoleSubtitle } from "../lib/rbacUi.js";
import { flagForTeam, teamLabel } from "../lib/teamFlags.js";
import { useImpersonationUsersList, type ImpersonationUser } from "../hooks/useImpersonationUsers.js";

function UserIdentity({
  name,
  roles,
  domains,
  team,
  align = "right",
}: {
  name: string;
  roles: ImpersonationUser["roles"];
  domains: ImpersonationUser["domains"];
  team?: ImpersonationUser["team"];
  align?: "left" | "right";
}): JSX.Element {
  const flag = flagForTeam(team);
  const region = teamLabel(team);
  return (
    <div
      className={clsx(
        "flex min-w-0 items-start gap-2",
        align === "right" ? "flex-row-reverse text-right" : "text-left",
      )}
    >
      <span
        className="mt-0.5 shrink-0 text-sm leading-none"
        title={region}
        aria-label={region}
      >
        {flag}
      </span>
      <div className="min-w-0 max-w-[11rem]">
        <div className="truncate text-sm font-medium leading-tight">{name}</div>
        <div className="truncate text-xs text-slate-500">{userRoleSubtitle({ roles, domains })}</div>
      </div>
    </div>
  );
}

function GroupLabel({ children }: { children: string }): JSX.Element {
  return (
    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
      {children}
    </div>
  );
}

/** Admin-only: view the app as another active user (all environments). */
export function UserSwitcher(): JSX.Element | null {
  const { user, canImpersonate, impersonate } = useAuth();
  const { data: users, isLoading } = useImpersonationUsersList();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  // Angel (Stef mtg 8/20): the view-as list is alphabetical and searchable.
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  const { cartoons, pilots, others } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (users ?? [])
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .toSorted((a, b) => a.name.localeCompare(b.name));
    return {
      cartoons: list.filter((u) => u.cartoon),
      pilots: list.filter((u) => u.pilot && !u.cartoon),
      others: list.filter((u) => !u.pilot && !u.cartoon),
    };
  }, [users, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!canImpersonate || isLoading || !user || !users?.length) return null;

  const renderOption = (u: ImpersonationUser) => {
    const selected = u.email === user.email;
    return (
      <button
        key={u.email}
        type="button"
        role="option"
        aria-selected={selected}
        disabled={busy || selected}
        className={clsx(
          "block w-full px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-60",
          selected && "bg-blue-50",
        )}
        onClick={async () => {
          if (selected) {
            setOpen(false);
            return;
          }
          setBusy(true);
          try {
            await impersonate(u.email);
            setOpen(false);
          } finally {
            setBusy(false);
          }
        }}
      >
        <UserIdentity
          name={u.name}
          roles={u.roles}
          domains={u.domains}
          team={u.team}
          align="left"
        />
      </button>
    );
  };

  const groupCount = [cartoons, pilots, others].filter((g) => g.length > 0).length;
  const showHeaders = groupCount > 1;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={busy}
        aria-label="View as another user"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() =>
          setOpen((o) => {
            if (!o) setQuery("");
            return !o;
          })
        }
        className={clsx(
          "flex items-center gap-1 rounded-md border border-transparent px-1 py-0.5 transition",
          "hover:border-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
          busy && "opacity-60",
        )}
      >
        <UserIdentity
          name={user.name || user.email}
          roles={user.roles}
          domains={user.domains}
          team={users.find((u) => u.email === user.email)?.team ?? null}
        />
        <span className="text-xs text-slate-400" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Users to view as"
          className="absolute right-0 z-50 mt-1 max-h-80 w-64 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          <div className="px-2 pb-1 pt-1.5">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or email…"
              aria-label="Search users"
              className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            />
          </div>
          {cartoons.length === 0 && pilots.length === 0 && others.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">No users match “{query}”.</p>
          ) : null}
          {showHeaders && cartoons.length > 0 ? <GroupLabel>Cartoons</GroupLabel> : null}
          {cartoons.map(renderOption)}
          {showHeaders && pilots.length > 0 ? <GroupLabel>Pilot</GroupLabel> : null}
          {pilots.map(renderOption)}
          {showHeaders && others.length > 0 ? <GroupLabel>Everyone else</GroupLabel> : null}
          {others.map(renderOption)}
        </div>
      )}
    </div>
  );
}
